import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ArrowBack } from "@mui/icons-material";
import axios from "axios";
import { addDays, format, startOfMonth } from "date-fns";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];
// "Upcoming" activities are dated in the future, so the default range looks ahead.
const UPCOMING_DAYS = 30;

const matchesType = (type, wanted) => {
  const key = String(wanted || "").toLowerCase();
  if (!key) return false;
  return [type.value, type.label].some((v) => String(v || "").toLowerCase().includes(key));
};

const localDateTimeValue = (date = new Date()) => {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "-" : format(parsed, "dd MMM yyyy, h:mm a");
};

function ActivityDialog({
  open,
  onClose,
  onSaved,
  domain,
  token,
  activityTypes,
  defaultTypeValue,
}) {
  const [contact, setContact] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [contactsLoading, setContactsLoading] = useState(false);
  const [typeId, setTypeId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(localDateTimeValue());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const defaultType = activityTypes.find((t) => matchesType(t, defaultTypeValue));
    setContact(null);
    setContactSearch("");
    setTypeId(defaultType?.id || activityTypes[0]?.id || "");
    setTitle(defaultType?.label ? `${defaultType.label}` : "");
    setDescription("");
    setDate(localDateTimeValue());
  }, [open, activityTypes, defaultTypeValue]);

  // Contacts are searched on the server; the list endpoint returns at most 100 per page.
  useEffect(() => {
    if (!open || !domain || !token) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setContactsLoading(true);
      try {
        const query = new URLSearchParams({ page_size: 100 });
        if (contactSearch.trim()) query.set("search", contactSearch.trim());
        const res = await axios.get(`${domain}crm/contacts/?${query.toString()}`, {
          headers: { Authorization: `token ${token}` },
          signal: controller.signal,
        });
        setContactOptions(res.data?.results ?? res.data ?? []);
      } catch {
        // keep the previous options on a failed/aborted search
      } finally {
        setContactsLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, domain, token, contactSearch]);

  const missingDefaultType =
    Boolean(defaultTypeValue) &&
    activityTypes.length > 0 &&
    !activityTypes.some((t) => matchesType(t, defaultTypeValue));

  const save = async () => {
    if (!contact?.id || !typeId || !title.trim()) {
      toast.error("Select contact, type, and title.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${domain}crm/activities/`,
        {
          contact: contact.id,
          type: typeId,
          title: title.trim(),
          description: description.trim(),
          date: new Date(date).toISOString(),
          completed: new Date(date) <= new Date(),
        },
        { headers: { Authorization: `token ${token}` } }
      );
      toast.success("Activity saved.");
      onSaved();
      onClose();
    } catch {
      toast.error("Failed to save activity.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Activity</DialogTitle>
      <DialogContent dividers>
        {missingDefaultType && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            No "{defaultTypeValue}" activity type is set up. Add it under CRM Settings →
            Activity Types, or pick another type below.
          </Alert>
        )}
        <Autocomplete
          options={contactOptions}
          value={contact}
          loading={contactsLoading}
          filterOptions={(options) => options}
          onChange={(_, value) => setContact(value)}
          onInputChange={(_, value, reason) => {
            if (reason === "input") setContactSearch(value);
          }}
          getOptionLabel={(c) =>
            [c.first_name, c.last_name].filter(Boolean).join(" ") ||
            c.company_name ||
            c.company ||
            "Contact"
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contact"
              placeholder="Type to search contacts"
              margin="normal"
              required
            />
          )}
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Activity Type</InputLabel>
          <Select label="Activity Type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            {activityTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Date & time"
          type="datetime-local"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={3}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save Activity
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ActivitiesPage() {
  const history = useHistory();
  const location = useLocation();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const params = new URLSearchParams(location.search);
  const defaultType = params.get("type") || "";
  const openCreate = params.get("create") === "1";
  const upcomingTo = format(addDays(new Date(), UPCOMING_DAYS), "yyyy-MM-dd");
  const firstOfMonth = format(startOfMonth(new Date()), "yyyy-MM-dd");

  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(Boolean(defaultType) || openCreate);
  const [filters, setFilters] = useState({
    from: firstOfMonth,
    to: upcomingTo,
    type: defaultType,
  });

  const headers = useMemo(() => ({ Authorization: `token ${token}` }), [token]);

  const loadLookups = useCallback(async () => {
    if (!domain || !token) return;
    const typesRes = await axios.get(`${domain}crm/activity-type/?page_size=100`, { headers });
    setActivityTypes(typesRes.data?.results ?? typesRes.data ?? []);
  }, [domain, token, headers]);

  const loadActivities = useCallback(async () => {
    if (!domain || !token) return;
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set("page", page + 1);
      query.set("page_size", rowsPerPage);
      query.set("start_date", filters.from);
      query.set("end_date", filters.to);
      query.set("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone || "");
      if (filters.type) query.set("type", filters.type);
      const res = await axios.get(`${domain}crm/activities/?${query.toString()}`, { headers });
      const data = res.data;
      const rows = data?.results ?? (Array.isArray(data) ? data : []);
      setActivities(rows);
      setCount(data?.count ?? rows.length);
    } catch {
      setActivities([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [domain, token, page, rowsPerPage, filters, headers]);

  useEffect(() => {
    loadLookups().catch(() => {});
  }, [loadLookups]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => history.push("/admin/crm/dashboard-v2")}
            variant="outlined"
          >
            Dashboard
          </Button>
          <Box>
            <Typography variant="h5" fontWeight={700}>Activities</Typography>
            <Typography variant="body2" color="text.secondary">
              {count.toLocaleString()} activities in the selected range
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<Add />} variant="contained" onClick={() => setDialogOpen(true)}>
          Create
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="From"
              type="date"
              value={filters.from}
              onChange={(e) => updateFilter("from", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="To"
              type="date"
              value={filters.to}
              onChange={(e) => updateFilter("to", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={filters.type} onChange={(e) => updateFilter("type", e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {activityTypes.map((type) => (
                  <MenuItem key={type.id} value={type.value || type.label?.toLowerCase()}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>Loading...</TableCell>
                </TableRow>
              ) : activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>No activities found.</TableCell>
                </TableRow>
              ) : (
                activities.map((activity) => (
                  <TableRow key={activity.id} hover>
                    <TableCell>{formatDateTime(activity.date)}</TableCell>
                    <TableCell>{activity.activity_name || "-"}</TableCell>
                    <TableCell>{activity.title || "-"}</TableCell>
                    <TableCell>{activity.contact_name || "-"}</TableCell>
                    <TableCell>{activity.company_name || "-"}</TableCell>
                    <TableCell>{activity.description || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      </Paper>

      <ActivityDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={loadActivities}
        domain={domain}
        token={token}
        activityTypes={activityTypes}
        defaultTypeValue={filters.type || defaultType}
      />
    </Box>
  );
}
